<template>
  <div class="ppt-page">
    <WorkbenchSidebar
      :logged-in="loggedIn"
      :balance="quotaBalance"
      @recharge="handleRecharge"
      @login="goLogin" />

    <div class="ppt-body">
      <div class="ppt-scroll">
        <WorkbenchTopbar
          :logged-in="loggedIn"
          :nick-name="userInfo.nickName"
          :avatar="userInfo.avatar"
          :unread-count="unreadCount"
          @notify="handleNotify"
          @redeem="handleRedeemCode"
          @login="goLogin"
          @register="goRegister"
          @logout="handleLogout" />

        <main class="ppt-content">
          <router-view
            ref="pptPage"
            :readonly="!loggedIn"
            :balance="quotaBalance"
            @require-login="goLogin"
            @require-recharge="handleRecharge"
            @ppt-submitted="onPptSubmitted" />
        </main>
      </div>
    </div>

    <RechargeModal
      :visible="showRecharge"
      :balance="quotaBalance"
      :user-name="displayName"
      :ledger-records="pointsLedger"
      @close="showRecharge = false"
      @open-redeem="handleRedeemCode"
      @recharge-success="onRechargeSuccess" />

    <RedeemCodeModal
      :visible="showRedeemCode"
      @close="showRedeemCode = false"
      @success="onRedeemSuccess" />

    <ArticleNotifyModal
      :visible="notifyVisible"
      @close="notifyVisible = false"
      @read-change="refreshUnreadCount" />

    <WorkbenchWelcomeModal
      :visible="welcomeVisible"
      :logged-in="loggedIn"
      :new-member-reward="welcomePromo.newMemberReward"
      :inviter-reward="welcomePromo.inviterReward"
      @close="closeWelcome"
      @login="onWelcomeLogin"
      @invite="onWelcomeInvite"
    />
  </div>
</template>

<script>
import WorkbenchSidebar from '@/components/workbench/WorkbenchSidebar.vue'
import WorkbenchTopbar from '@/components/workbench/WorkbenchTopbar.vue'
import RechargeModal from '@/components/recharge/RechargeModal.vue'
import RedeemCodeModal from '@/components/recharge/RedeemCodeModal.vue'
import { getQuotaBalance, getQuotaLogs } from '@/api/portal/member'
import { logout } from '@/api/portal/auth'
import { isLoggedIn, getMember, clearAuth, getCachedQuotaBalance, setCachedQuotaBalance } from '@/utils/auth'

import portalAuthMixin from '@/mixins/portalAuthMixin'
import articleNotifyMixin from '@/mixins/articleNotifyMixin'
import workbenchWelcomeMixin from '@/mixins/workbenchWelcomeMixin'
import redeemCodeMixin from '@/mixins/redeemCodeMixin'

export default {
  name: 'PptLayout',
  mixins: [portalAuthMixin, articleNotifyMixin, workbenchWelcomeMixin, redeemCodeMixin],
  components: { WorkbenchSidebar, WorkbenchTopbar, RechargeModal, RedeemCodeModal },
  data() {
    const cachedBalance = getCachedQuotaBalance()
    return {
      loggedIn: isLoggedIn(),
      quotaBalance: cachedBalance != null ? cachedBalance : 0,
      showRecharge: false,
      pointsLedger: [],
      userInfo: getMember() || { nickName: '访客' }
    }
  },
  computed: {
    displayName() {
      return this.userInfo.nickName || this.userInfo.userName || '用户'
    }
  },
  mounted() {
    if (this.loggedIn) {
      this.refreshQuotaBalance()
    }
  },
  methods: {
    handleRecharge() {
      if (!this.loggedIn) {
        this.goLogin()
        return
      }
      this.showRecharge = true
      getQuotaLogs({ page: 1, pageSize: 20 }).then(res => {
        this.pointsLedger = (res.data || []).map(item => ({
          time: item.createTime,
          type: item.changeTypeLabel || item.changeType,
          amount: item.changeAmount,
          balance: item.balanceAfter
        }))
      }).catch(() => {})
    },
    refreshQuotaBalance() {
      if (!this.loggedIn) {
        return
      }
      getQuotaBalance().then(res => {
        const data = res.data || {}
        this.quotaBalance = data.available != null ? data.available : 0
        setCachedQuotaBalance(this.quotaBalance)
      }).catch(() => {})
    },
    onRechargeSuccess() {
      this.showRecharge = false
      this.refreshQuotaBalance()
      this.refreshPptEntitlement()
    },
    onPptSubmitted() {
      this.refreshQuotaBalance()
      this.refreshPptEntitlement()
    },
    refreshPptEntitlement() {
      const page = this.$refs.pptPage
      if (page && typeof page.loadEntitlement === 'function') {
        page.loadEntitlement()
      }
    },
    handleLogout() {
      logout().finally(() => {
        clearAuth()
        this.loggedIn = false
        this.quotaBalance = 0
        this.userInfo = { nickName: '访客' }
        this.$router.push('/')
      })
    }
  }
}
</script>

<style scoped>
.ppt-page {
  display: flex;
  min-height: 100vh;
  background: #f5f6fa;
}

.ppt-body {
  flex: 1;
  min-width: 0;
}

.ppt-scroll {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.ppt-content {
  flex: 1;
  padding: 20px 24px 40px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}
</style>
